import React, { useEffect, useState } from 'react';
import { Info } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';

// COMPONENTS
import RowLabel from '@/main/components/common/rowLabel/rowLabel';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';

// REDUX
import { claimApi } from '@/main/api';
import { ClaimTooltipViewResponse } from '@/main/models/response';
import { getTranslateByKey, isEmpty } from '@/main/services/utils';
import { formatCurrency } from '@/main/services/utilsNumber';

import * as S from './styles';
import { useUserReducer } from '@/main/store/user';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';

interface IClaimDetailsSearchClaimProps {
  openModalClaimDetailsSearchClaim: boolean;
  guidClaim: string;
  showPrices?: boolean;
  setOpenModalClaimDetailsSearchClaim: (value: boolean) => void;
}

const useStylesTooltip = makeStyles({
  popperCustom: { marginLeft: 15 }
});

const ClaimDetailsSearchClaim: React.FC<IClaimDetailsSearchClaimProps> = ({
  openModalClaimDetailsSearchClaim,
  guidClaim,
  showPrices = true,
  setOpenModalClaimDetailsSearchClaim
}: IClaimDetailsSearchClaimProps) => {
  const classesTooltip = useStylesTooltip();

  const [data, setData] = useState<ClaimTooltipViewResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  async function loadTooltip(guidClaim: string) {
    setIsLoading(true);

    claimApi
      .getClaimTooltipData(guidClaim)
      .then(response => {
        const { result } = response.data;
        if (isEmpty(result)) return
        setData(result);
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (openModalClaimDetailsSearchClaim && guidClaim) {
      loadTooltip(guidClaim)
    }
  }, [openModalClaimDetailsSearchClaim, guidClaim]);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpenModalClaimDetailsSearchClaim(true);
  };

  const { currentUser: userReducer } = useUserReducer();
  const userIsSupplier = userReducer?.idCompanyType === CompanyTypeEnum.Supplier;
  const canShowPrices = showPrices && !userIsSupplier;

  return (
    <>
      <TooltipComponent
        title={`${getTranslateByKey('information.clickToSeeClaimDetails')}`}
        placement="top"
        stylePopper={classesTooltip.popperCustom}>
        <IconButton
          size="medium"
          color="primary"
          style={{ marginLeft: 5 }}
          onClick={handleButtonClick}
          id="id-claimDetailsSearchClaim-btn-seeClaimDetailsSearchClaim">
          <Info />
        </IconButton>
      </TooltipComponent>
      <KogModalSideComponent
        withStopPropagation
        open={openModalClaimDetailsSearchClaim}
        title={getTranslateByKey('information.claimDetails')}
        closeModal={() => {
          setOpenModalClaimDetailsSearchClaim(false);
        }}>
        <S.Container>
          {isLoading ? (
            <LoadingComponent />
          ) : data !== null ? (
            <S.ContainerInfo>
              <RowLabel
                label={getTranslateByKey('information.assessmentType')}
                content={data.assessmentTypeName}
              />
              <RowLabel
                label={getTranslateByKey('information.claimStatus')}
                content={data.claimStatus}
              />
              <RowLabel
                label={getTranslateByKey('information.Buyparts')}
                content={data.buyParts}
              />
              <RowLabel
                label={getTranslateByKey('information.Infloor')}
                content={data.inFloor}
              />
              <RowLabel
                label={getTranslateByKey('information.claimType')}
                content={data.claimTypeName}
              />
              <RowLabel
                label={getTranslateByKey('information.policyNumber')}
                content={data.policyNumber}
              />
              <RowLabel
                label={getTranslateByKey('information.licensePlate')}
                content={data.vehicleRegistration}
              />
              <RowLabel
                label={getTranslateByKey('information.claimDate')}
                content={data.formattedClaimDate}
              />
              <RowLabel
                label={getTranslateByKey('information.authorizationDate')}
                content={data.formattedAuthorizationDate}
              />
              <RowLabel
                label={getTranslateByKey('information.approvedClaim')}
                content={data.approved ?
                  getTranslateByKey('information.yes') :
                  getTranslateByKey('information.no')
                }
              />
              <RowLabel
                label={getTranslateByKey('information.authorizedBy')}
                content={data.authorizedBy}
              />
              {canShowPrices && (
                <RowLabel
                  label={getTranslateByKey('information.deductible')}
                  content={data.deductible ?
                    formatCurrency(data.deductible) :
                    ''}
                />
              )}
              <RowLabel
                label={getTranslateByKey('information.deductibleType')}
                content={data.deductibleType}
              />
              <RowLabel
                label={getTranslateByKey('information.claimNumberSiniestro')}
                content={data.adminData}
              />
              {canShowPrices && (
                <>
                  <RowLabel
                    label={getTranslateByKey('information.vehicleValue')}
                    content={data.vehicleValue ?
                      formatCurrency(data.vehicleValue) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingValuePerHour')}
                    content={data.paintHourValue ?
                      formatCurrency(data.paintHourValue) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingMaterialAmount')}
                    content={data.paintMaterialAmount ?
                      formatCurrency(data.paintMaterialAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingLabourAmount')}
                    content={data.paintLabourAmount ?
                      formatCurrency(data.paintLabourAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairValuePerHour')}
                    content={data.repairHourValue ?
                      formatCurrency(data.repairHourValue) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairLabourAmount')}
                    content={data.repairLabourAmount ?
                      formatCurrency(data.repairLabourAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.totalPartsAmount')}
                    content={data.totalPartsAmount ?
                      formatCurrency(data.totalPartsAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.totalOtherAmount')}
                    content={data.totalOtherAmount ?
                      formatCurrency(data.totalOtherAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairAndPaintTotalAmountWithVAT')}
                    content={data.totalRepairWithoutVAT ?
                      formatCurrency(data.totalRepairWithoutVAT) :
                      ''}
                  />
                </>
              )}
            </S.ContainerInfo>
          ) : (
            <ResultNotFoundComponent />
          )}
          <S.AreaButtons>
            <SecondaryButton
              id="id-claimDetailsSearchClaim-btn-back"
              onClick={() => setOpenModalClaimDetailsSearchClaim(false)}
            >
              {getTranslateByKey('information.back')}
            </SecondaryButton>
          </S.AreaButtons>
        </S.Container>
      </KogModalSideComponent>
    </>
  );
};

export default ClaimDetailsSearchClaim;
